
/**
 * --- External Imports ---
 */
import { FC, useContext, useMemo } from 'react';

/**
 * --- Internal Imports ---
 */
import { CheckboxGroupFormField, IFormField, IHasChildren as IHasFieldChildren, RadioGroupFormField, Species } from '../../../api/dtos/dtos';
import MapFormContext from '../../../contexts/data/MapFormContext';
import { SpeciesContext } from '../../../contexts/data/SpeciesContext';
import { SpeciesFormsContext } from '../../../contexts/data/SpeciesFormsContext';
import useGetSpeciesForms from '../../../hooks/data/useGetSpeciesForms';
import { IHasChildren } from '../../common/types/IHasChildren';
import { getFieldType } from '../../forms/fields/species-fields/FormFieldComponent';


/**
 * --- Interfaces ---
 */
interface ISpeciesFormProviderProps extends IHasChildren { }

/**
 * --- Species Form Provider ---
 */
const SpeciesFormProvider: FC<ISpeciesFormProviderProps> = ({ 
    children 
}) => {

    // 
    // -- Contexts --
    const { species: { species } } = useContext(SpeciesContext);
    const { drawerOpen, siteEventType, drawerMaximised } = useContext(MapFormContext);

    const condition = !!(species !== Species.None);

    //
    // -- Queries --
    const speciesFormsQuery = useGetSpeciesForms(
        condition ? { speciesType: species } : undefined, 
        { revalidateOnFocus: false }
    );

    //
    // -- Callbacks --
    const getFormFields = (formFields: IFormField[]): Record<string, IFormField> => {
        if (formFields.length === 0) return {};

        let fields: Record<string, IFormField> = {};

        for (let field of formFields) {
            const fieldType = getFieldType(field);

            if (!field.name) continue;

            let children: IFormField[] = [];

            // Special case for checkbox groups
            if (fieldType === "CheckboxGroupFormField") {
                let checkboxes: Record<string, IFormField> = {};

                // Iterate all checkboxes in the the checkbox group
                for (let ch of ((field as CheckboxGroupFormField).checkboxes ?? [])) {
                    children = [ 
                        ...children, 
                        ...(ch.children ?? []) 
                    ];

                    if (ch.name) {
                        checkboxes = {
                            ...checkboxes,
                            [ch.name]: ch
                        };
                    }
                }
                
                fields = {
                    ...fields,                      // Append
                    ...checkboxes,                  // Add all checkboxes of the checkox group
                    ...getFormFields(children),     // Add all children of all checkboxes from the checkbox group
                    [field.name]: field             // Add this checkbox group
                }

            // Special case for radio group
            } else if (fieldType === "RadioGroupFormField") {
                let radios: Record<string, IFormField> = {};

                for (let rd of ((field as RadioGroupFormField).radios ?? [])) {
                    children = [
                        ...children,
                        ...(rd.children ?? [])
                    ];

                    if (rd.name) {
                        radios = {
                            ...radios,
                            [rd.name]: rd
                        };
                    }
                }

                fields = {
                    ...fields,
                    ...radios,
                    ...getFormFields(children),
                    [field.name]: field 
                };

            // All other fields
            } else {    
                children = (field as IHasFieldChildren).children?.map(x => x) ?? [];

                fields = {
                    ...fields,
                    ...getFormFields(children),
                    [field.name!]: field
                }
            }
        }
        
        return fields;
    }

    //
    // -- Cached --
    const selectedForm = useMemo(() => {
        if (!siteEventType) return undefined;
        return siteEventType && speciesFormsQuery.data?.find(x => x.siteEventType === siteEventType);
    }, [siteEventType, speciesFormsQuery.data]);

    const selectedFormFields = useMemo(() => {
        if (!selectedForm) return undefined;

        let fields: Record<string, IFormField> = {};
        
        for (let question of (selectedForm.speciesFormQuestions ?? [])) {
            fields = {
                ...fields, 
                ...getFormFields(question.speciesFormFields ?? [])
            };
        }

        return fields;
    }, [selectedForm, getFormFields]);

    const selectingLocation = useMemo(() => {
        return !!(drawerOpen && !drawerMaximised);
    }, [drawerOpen, drawerMaximised]);
    
    const canClickMap = useMemo(() => {
        return !!(drawerOpen && siteEventType && selectedForm && !selectingLocation);
    }, [drawerOpen, siteEventType, selectedForm, selectingLocation]);


    const context = useMemo(() => {
        return { 
            ...speciesFormsQuery,
            selectedForm: selectedForm,
            selectedFormFields: selectedFormFields,
            canClickMap: canClickMap,
            selectingLocation: selectingLocation
        };
    }, [speciesFormsQuery, selectedForm, selectedFormFields, canClickMap, selectingLocation]);

    return (
        <SpeciesFormsContext.Provider value={context}>
            {children}            
        </SpeciesFormsContext.Provider>
    )
}

export default SpeciesFormProvider